<template>
    <div 
        ref="draggableElement"
        class="draggable-wrapper"
        :class="{ 'dragging': isDragging }"
        @mousedown="startDrag"
        @touchstart="startDrag"
    >
        <slot></slot>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, onUnmounted } from 'vue'
import appStore from '../../../store/app.store'

const props = defineProps({
    note: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['update:note','startDrag','drag','endDrag', 'mouseout'])

const draggableElement = ref(null)
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const elementStart = ref({ x: 0, y: 0 })
const parentOffset = ref({ x: 0, y: 0 })

// Calculate parent offset to avoid mouse-pointer offset issues
const calculateParentOffset = () => {
    if (draggableElement.value) {
        const parent = draggableElement.value.offsetParent
        if (parent) {
            const parentRect = parent.getBoundingClientRect()
            parentOffset.value = {
                x: parentRect.left,
                y: parentRect.top
            }
        }
    }
}

const startDrag = (event) => {
    emit('startDrag', event)
    // Only start drag if clicking on the handle or the element itself
    const target = event.target
    const isHandle = target.classList.contains('handle') || 
                    target.closest('.handle') || 
                    target.classList.contains('cursor-move') ||
                    target.closest('.cursor-move')
    
    // Allow dragging from anywhere on the wrapper
    if (!isHandle) {
        return
    }

    appStore.state.disableEvents = true;
    event.preventDefault()
    event.stopPropagation()
    isDragging.value = true
    
    calculateParentOffset()
    
    // Get initial mouse/touch position
    const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX
    const clientY = event.type.includes('touch') ? event.touches[0].clientY : event.clientY
    
    dragStart.value = {
        x: clientX,
        y: clientY
    }
    
    // Store initial element position
    elementStart.value = {
        x: props.note.x,
        y: props.note.y
    }
    
    // Add global event listeners
    document.addEventListener('mousemove', drag)
    document.addEventListener('touchmove', drag, { passive: false })
    document.addEventListener('mouseup', endDrag)
    document.addEventListener('touchend', endDrag)
    document.addEventListener('mouseout', mouseout)
}
const mouseout = (event) => {
    emit('mouseout', event)
}

const drag = (event) => {
    if (!isDragging.value) return
    emit('drag', event)
    event.preventDefault()
    
    const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX
    const clientY = event.type.includes('touch') ? event.touches[0].clientY : event.clientY
    
    // Calculate the difference from the initial drag position
    const deltaX = clientX - dragStart.value.x
    const deltaY = clientY - dragStart.value.y
    
    // Update note position
    let newX = elementStart.value.x + deltaX
    let newY = elementStart.value.y + deltaY
    
    // Clamp to prevent moving outside of the container
    let parentWidth = window.innerWidth;
    let parentHeight = window.innerHeight;
    let elWidth = props.note.width || 0;
    let elHeight = props.note.height || 0;

    if (draggableElement.value) {
        const child = draggableElement.value.firstElementChild;
        const isFixed = child && window.getComputedStyle(child).position === 'fixed';
        
        if (!isFixed && draggableElement.value.offsetParent) {
            parentWidth = draggableElement.value.offsetParent.clientWidth || window.innerWidth;
            parentHeight = draggableElement.value.offsetParent.clientHeight || window.innerHeight;
        }

        elWidth = (child ? child.offsetWidth : draggableElement.value.offsetWidth) || elWidth;
        elHeight = (child ? child.offsetHeight : draggableElement.value.offsetHeight) || elHeight;
    }

    newX = Math.max(0, Math.min(newX, parentWidth - elWidth));
    newY = Math.max(0, Math.min(newY, parentHeight - elHeight));
    
    // Emit the updated note with new coordinates
    emit('update:note', {
        ...props.note,
        x: newX,
        y: newY
    })
}

const endDrag = (event) => {
    appStore.state.disableEvents = false;
    if (!isDragging.value) return
    
    isDragging.value = false
    emit('endDrag', event)
    // Remove global event listeners
    document.removeEventListener('mousemove', drag)
    document.removeEventListener('touchmove', drag)
    document.removeEventListener('mouseup', endDrag)
    document.removeEventListener('touchend', endDrag)
}

// Cleanup on unmount
onUnmounted(() => {
    document.removeEventListener('mousemove', drag)
    document.removeEventListener('touchmove', drag)
    document.removeEventListener('mouseup', endDrag)
    document.removeEventListener('touchend', endDrag)
})
</script>

<style scoped>
.handle {
    cursor: move;
    user-select: none;
}

.handle.dragging {
    cursor: grabbing;
    z-index: 1000;
}

.handle.dragging * {
    pointer-events: none;
}

/* Ensure the handle is always draggable */
.handle .handle {
    cursor: move;
}

.handle.dragging .handle {
    cursor: grabbing;
}
</style>